import { Button } from '@mui/material';
import { difference } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { CustomerReportModel, StressIncrementType } from '../../../../../../Generated/Commodity-Management-Api';
import { useApplicationDispatch } from '../../../../../../Redux/ReduxStore';
import { setCustomerReportUserOverride } from '../../../../../../Redux/Slices/CommodityManagementSlice';
import { StoneXDivider, StoneXRow } from '../../../../../StoneX';
import { StoneXModal, StoneXSelect, StoneXTextField } from '../../../../../StoneXMui';
import { StoneXTransferList } from '../../../../../StoneXMui/StoneXTransferList/StoneXTransferList';
import { ExposureColumns, ExposureReportColumn, ExposureReportConfiguration, ExposureReportUserOverride } from './Types';

interface ExposureReportSettingsModalProps {
    customerReport: CustomerReportModel;
    open: boolean;
    onClose: () => void;
    onSave: (customerReport: CustomerReportModel) => void;
}

export default function ExposureReportSettingsModal(props: ExposureReportSettingsModalProps) {
    const { customerReport, open, onClose, onSave } = props;
    const dispatch = useApplicationDispatch();

    const stressIncrementTypes = useMemo<StressIncrementType[]>(() => [StressIncrementType.Percent, StressIncrementType.Price], []);

    const reportConfiguration = useMemo(
        () => JSON.parse(customerReport.configuration!) as ExposureReportConfiguration,
        [customerReport],
    );

    const [userOverrides, setUserOverrides] = useState<ExposureReportUserOverride | undefined>();

    const [hiddenColumns, setHiddenColumns] = useState<ExposureReportColumn[]>([]);
    const [activeColumns, setActiveColumns] = useState<ExposureReportColumn[]>([]);
    const [updatedActiveColumns, setUpdatedActiveColumns] = useState<ExposureReportColumn[] | undefined>();

    const [stressIncrementType, setStressIncrementType] = useState<StressIncrementType>(StressIncrementType.Percent);
    const [stressIncrementsText, setStressIncrementsText] = useState<string>('');

    const exposureColumnOrderMap = useMemo<{ [key: string]: number }>(() => {
        const keys = Object.keys(ExposureColumns);
        const map: { [key: string]: number } = {};
        keys.forEach((value: string, index) => (map[value] = index));
        return map;
    }, []);

    useEffect(() => {
        const parsedOverrides = customerReport.userOverrides
            ? (JSON.parse(customerReport.userOverrides) as ExposureReportUserOverride)
            : undefined;
        setUserOverrides(parsedOverrides);
    }, [customerReport]);

    useEffect(() => {
        const isKnownColumn = (column: ExposureReportColumn) => ExposureColumns[column] !== undefined;
        const availableColumnsList = (reportConfiguration.columns?.available ?? []).filter(isKnownColumn);
        const resolvedActiveColumns = (userOverrides?.columns?.active ?? reportConfiguration.columns?.active ?? []).filter(isKnownColumn);
        setHiddenColumns(difference(availableColumnsList, resolvedActiveColumns));
        setActiveColumns(resolvedActiveColumns);
        setUpdatedActiveColumns(undefined);

        const resolvedIncrementType = userOverrides?.behavior?.stressIncrementType ?? reportConfiguration.behavior?.stressIncrementType ?? StressIncrementType.Percent;
        const resolvedIncrements = userOverrides?.behavior?.stressIncrements ?? reportConfiguration.behavior?.stressIncrements ?? [];
        setStressIncrementType(resolvedIncrementType);
        setStressIncrementsText(resolvedIncrements.join(', '));
    }, [reportConfiguration, userOverrides]);

    function onTransferListUpdate(_hiddenItems: ExposureReportColumn[], activeItems: ExposureReportColumn[]) {
        setUpdatedActiveColumns(activeItems);
    }

    function reset() {
        setUserOverrides(undefined);
    }

    function save() {
        const activeColumnsToSave = updatedActiveColumns ?? activeColumns;
        const stressIncrements = stressIncrementsText
            .split(',')
            .map((value) => Number(value.trim()))
            .filter((value) => !Number.isNaN(value));

        const userSettings: ExposureReportUserOverride = {
            behavior: {
                stressIncrementType,
                stressIncrements,
            },
            columns: {
                active: activeColumnsToSave,
            },
        };

        const userOverridesJson = JSON.stringify(userSettings);

        CommodityManagementApi.updateCustomerReportUserOverrides(undefined, customerReport.customerReportId, userOverridesJson);

        dispatch(
            setCustomerReportUserOverride({
                customerReportId: customerReport.customerReportId,
                userOverrides: userOverridesJson,
            }),
        );

        onSave({ ...customerReport, userOverrides: userOverridesJson });
    }

    return (
        <StoneXModal header={customerReport.reportName + ' Report Configuration'} width='850px' open={open} onClose={onClose}>
            <h2>Stress Scenario</h2>

            <StoneXRow>
                <StoneXSelect
                    label="Stress Increment Type"
                    options={stressIncrementTypes}
                    getId={(x) => x}
                    getOptionLabel={(x) => x}
                    value={stressIncrementType}
                    onChange={(value) => setStressIncrementType(value ?? StressIncrementType.Percent)}
                />
            </StoneXRow>

            <StoneXRow>
                <StoneXTextField
                    label="Stress Increments"
                    helperText="Comma-separated values, e.g. 10, 25, 50"
                    value={stressIncrementsText}
                    onUpdate={setStressIncrementsText}
                />
            </StoneXRow>

            <StoneXDivider />

            <h2>Columns</h2>

            <p>Click on one or multiple items to select, then use the arrows to move.</p>

            <StoneXTransferList
                leftList={hiddenColumns}
                leftHeader="Available Columns"
                rightList={activeColumns}
                rightHeader="Active Columns"
                getId={(x) => ExposureColumns[x].colId}
                getSortingOrder={(x) => exposureColumnOrderMap[x]}
                getOptionLabel={(x) => ExposureColumns[x].headerName ?? x}
                onUpdate={onTransferListUpdate}
            />

            <StoneXRow justify="end" marginDirection='top'>
                <Button variant="outlined" onClick={onClose}>Cancel</Button>
                <Button variant="outlined" onClick={reset}>Reset</Button>
                <Button onClick={save}>Set</Button>
            </StoneXRow>
        </StoneXModal>
    );
}
